import { MessageInput } from "@/components/messages/MessageInput"
import { MessageList } from "@/components/patient/messages/MessageList"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Skeleton } from "@/components/ui/skeleton"
import { useMessages } from "@/hooks/messages/useMessages"
import { useAuth } from "@/hooks/useAuth"
import { Assessment } from "@/types/database/assessment"
import { isValidProfile } from "@/types/database/profile"
import { Loader2 } from "lucide-react"
import { useState } from "react"

interface MessageThreadProps {
  assessment: Assessment
}

export const MessageThread = ({ assessment }: MessageThreadProps) => {
  const { messages, sendMessage, isLoading } = useMessages(assessment.id)
  const [newMessage, setNewMessage] = useState("")
  const { user } = useAuth()

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return
    await sendMessage(newMessage)
    setNewMessage("")
  }

  const profile = assessment.profiles
  const patientName = isValidProfile(profile) 
    ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unknown Patient'
    : 'Unknown Patient'

  return (
    <div className="flex flex-col h-full border rounded-lg">
      {/* Conversation Header */}
      <div className="border-b p-4 bg-muted/50">
        <h3 className="font-semibold text-lg">{patientName}</h3>
        <p className="text-sm text-muted-foreground">
          Assessment: {new Date(assessment.created_at || '').toLocaleDateString()}
        </p>
      </div>

      {/* Messages Area */}
      <ScrollArea className="flex-1 h-[calc(100vh-300px)]">
        <div className="p-4">
          {isLoading ? (
            <div className="space-y-4">
              {/* Loading header with spinner */}
              <div className="flex items-center justify-center py-8">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Loading conversation...</p>
                </div>
              </div>
              
              {/* Message skeleton */}
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                    <div className="flex flex-col space-y-2 max-w-xs">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className={`h-16 ${i % 2 === 0 ? 'w-48' : 'w-56'}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <MessageList messages={messages} currentUserId={user?.id} />
          )}
        </div>
      </ScrollArea>

      {/* Message Input */}
      <div className="p-4 border-t mt-auto bg-background">
        <MessageInput
          value={newMessage}
          onChange={setNewMessage}
          onSend={handleSendMessage}
          isLoading={isLoading}
        />
      </div>
    </div>
  )
}